<template>
  <div class="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-[520px]">
      <div class="items-center bg-white px-6 md:px-12 pb-6 shadow sm:rounded-lg">
        <SectionHeader
          class="mx-auto !justify-center pb-8 pt-14"
          :title="__('Sign In')"
        >
        </SectionHeader>
        <div>
          <div>
            <div class="pb-8">
              <div
                  class="mx-auto flex w-full justify-center gap-2 rounded-lg bg-white"
              >
                  <button
                      class="w-full bg-[#FAFAFA] flex-1 rounded-md px-8 py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5`"
                      @click="redirectTo('candidateLogin')"
                  >
                    {{ __('Candidate') }}
                  </button>
                  <button
                      class="w-full selected bg-[#F6F3F9] flex-1 rounded-md px-8 py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5"
                      @click="redirectTo('companyLogin')"
                  >
                    {{ __('Company') }}
                  </button>
              </div>
            </div>
            <div>
              <div class="space-y-12">
                <form @submit.prevent="handleSubmit">
                  <div class="flex flex-col">
                    <InputEmail
                      id="password_current"
                      v-model:value="values.login"
                      v-model:errors="errors.login"
                      :label="__('Email')"
                      :placeholder="__('Your email')"
                      class="pb-4"
                      required
                    />

                    <InputPassword
                      id="password"
                      v-model:value="values.password"
                      v-model:errors="errors.password"
                      :label="__('Password')"
                      :placeholder="__('Type your password')"
                      class="pb-8"
                      required
                    />
                    <div class="flex flex-wrap items-center justify-between gap-2 mb-8">
                      <InputCheckbox
                        id="remember"
                        v-model:value="values.remember"
                        v-model:errors="errors.remember"
                        :label="__('Remember me')"
                      />

                      <a @click="redirectTo('forgot')" class="text-md font-medium cursor-pointer text-primary underline">
                        {{ __('Forgot your password?') }}
                      </a>
                    </div>
                    <div v-if="!config_general_single_company" class="text-center">
                      <p class="text-sm">
                           {{ __("Don't have an account?") }}
                        <a @click="redirectTo('register')" class="cursor-pointer font-medium text-primary whitespace-nowrap">{{ __('Register now')}}</a>
                      </p>
                    </div>
                    <div
                      v-if="errorMessage || successMessage"
                      class="mt-8 flex flex-col"
                    >
                      <div
                        v-if="errorMessage"
                        class="flex items-center gap-1 rounded-lg border border-red-400 bg-red-100 p-4 text-red-700"
                      >
                        <span
                          class="material-symbols-outlined text-xl text-red-600"
                          style="font-variation-settings: 'FILL' 1"
                        >
                          error
                        </span>
                        <span>{{ errorMessage }}</span>
                        <button
                            type="button"
                            v-if="resendFromError"
                            @click.prevent="handleResendEmailClick"
                            class="font-medium underline cursor-pointer text-primary"
                        >
                          {{ __('Resend Email') }}
                        </button>

                        <button
                            type="button"
                            v-if="allowReevaluation"
                            @click.prevent="requestNewEvaluation"
                            class="font-medium underline cursor-pointer text-primary"
                            :disabled="isReevaluating"
                        >
                          <Icon
                            v-if="isReevaluating"
                            name="progress_activity"
                            class="animate-spin text-base text-primary mx-10"
                          />
                          <span v-else>{{ __('Request Reevaluation') }}</span>
                        </button>
                      </div>

                      <div
                        v-if="successMessage"
                        class="flex items-center gap-2 rounded-lg border border-green-400 bg-green-100 p-4 text-green-700"
                      >
                        <span
                          class="material-symbols-outlined text-xl text-green-600"
                          style="font-variation-settings: 'FILL' 1"
                        >
                          check_circle
                        </span>
                        <span>{{ successMessage }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    class="mt-4 flex flex-col gap-2 md:flex-row-reverse md:justify-center items-center"
                  >
                    <Button
                      v-if="showResendButton"
                      @click="resendVerificationEmail"
                      class="rounded-lg  px-4 py-2 font-medium  transition "
                      variant="outline"
                    >
                      {{ __('Resend Verification Email')}}
                    </Button>
                    <Button
                      v-else
                      class="flex items-center justify-center gap-1.5"
                      :type="isLoading ? 'button' : 'submit'"
                      :disabled="isLoading"
                      style="width: 10rem !important"
                    >
                      <Icon
                        v-if="isLoading"
                        class="animate-spin text-lg"
                        name="progress_activity"
                      />
                      <span>{{ __('Sign In') }}</span>
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, watch, onMounted, nextTick} from 'vue';
  import { __ } from '@/helpers';
  import { Button } from '../../../../components/block/button';
  import { Icon } from '../../../../components/block/icon';
  import { InputPassword } from '../../../../components/form/input-password';
  import { InputEmail } from '../../../../components/form/input-email';
  import { SectionHeader } from '../../../components/section-header';
  import { InputCheckbox } from '../../../../components/form/input-checkbox';
  import axios from 'axios';
import {useToast} from "vue-toastification";

const props = defineProps({
  successMessage: {
    type: String,
    default: null
  },
  errorMessage: {
    type: String,
    default: null
  },
  flash: String,
  config_general_single_company: {
    type: Number,
    required: true,
  }
});

const toast = useToast();

const message = ref(props.flash);

const values = ref({
  login: '',
  password: '',
  remember: false
});

const routes = {
  companyLogin: 'front.auth.company.login',
  candidateLogin: 'login.user',
  register: 'front.auth.company.register',
  forgot: 'company.password.forgot'
}

const redirectTo = (routeName) => {
  window.location.href = route(routes[routeName]);
}

const errors = ref({
  login: [],
  password: [],
  remember: []
});

const isLoading = ref(false);

const errorMessage = ref('');
const successMessage = ref('');
const showResendButton = ref(false);
const resendFromError = ref(false);
const allowReevaluation = ref(false);
const isReevaluating = ref(false);

const handleResendEmailClick = () => {
    resendVerificationEmail();
  }

const resendVerificationEmail = async () => {
  if (!values.value.login) {
    errorMessage.value = 'Email is required.';
    successMessage.value = '';
    return;
  }

  errorMessage.value = '';
  successMessage.value = '';

  try {
    const response = await axios.post(route('company-resend-verification-token'), {
      email: values.value.login,
    });
    errorMessage.value = '';
    successMessage.value = response.data.message;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Something went wrong.';
    successMessage.value = '';
  }
};

const requestNewEvaluation = async () => {
  try {
    isReevaluating.value = true;
    const response = await axios.post(route('company.request.reevaluation'), {
      email: values.value.login,
    });

    errorMessage.value = '';
    successMessage.value = response.data.message || __('Your request was sent successfully.');
  } catch (error) {
    errorMessage.value = error.response?.data?.message || __('Failed to request reevaluation.');
  } finally {
    isReevaluating.value = false;
  }
};


async function handleSubmit() {
    const requiredFieldKeys = ['login', 'password'];
    let firstElement;

    requiredFieldKeys.forEach((key) => {
      if (!values.value[key]) {
        errors.value[key] = [__('This field is required')];

        const element = document.getElementById(key);

        if (element && !firstElement) {
          firstElement = element;
        }
      }
    });

    if (Object.values(errors.value).some((error) => error.length)) {
      if (firstElement) {
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    isLoading.value = true;
    errorMessage.value = '';
    successMessage.value = '';
    showResendButton.value = false;
    resendFromError.value = false;
    allowReevaluation.value = false;

    try {
      const response = await axios.post(route('company.login'), values.value);

      if (response.data.status === 'success' || response.data.status === '2fa_required') {
        errorMessage.value = '';
        window.location.href = response.data.redirect;
      }
    } catch (error) {
      successMessage.value = '';
      allowReevaluation.value = error.response?.data?.revetting === true;
      if (error?.response?.status === 403 && error.response.data.resend) {
        errorMessage.value = error.response.data.message;
        showResendButton.value = true;
        resendFromError.value = false;
      } else if (error?.response?.data?.message && error.response?.data?.resend_email) {
        errorMessage.value = error.response.data.message;
        resendFromError.value = true;
      } else if(error?.response?.data?.message){
        errorMessage.value = error.response.data.message;
        resendFromError.value = false;
      } else {
        errorMessage.value = __(
            'An unexpected error has occurred. Please try again later.'
        );
        resendFromError.value = false;
      }
    }

    isLoading.value = false;
  }

  onMounted(() => {
    setTimeout(() => {
      if (message.value) {
        nextTick(() => {
          toast.success(message.value, {
            timeout: 7000,
          });

          setTimeout(() => {
            axios.delete(route("company-clear-reset-password-message")).catch(() => {});
            message.value = "";
          }, 5000);
        });
      }
    }, 500);
  });
</script>